import { zodResolver } from "@hookform/resolvers/zod";
import { FC, useEffect, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { FormattedMessage, IntlShape, useIntl } from "react-intl";
import { z } from "zod";

import { Button } from "~/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "~/components/ui/dialog";
import { Input } from "~/components/ui/input";
import { Label } from "~/components/ui/label";
import { Switch } from "~/components/ui/switch";

import { useQueryClient } from "@tanstack/react-query";
import dayjs from "dayjs";
import { ChevronLeftIcon } from "lucide-react";
import { getListProjectsQueryKey } from "~/api/folders/folders";
import { useGetOrganizationSettings } from "~/api/organizations/organizations";
import {
  getGetProjectQueryKey,
  useGetProject,
  useUpdateProject,
} from "~/api/projects/projects";
import DayPicker from "~/components/DayPicker";
import { useOrganizationParams } from "~/helpers";
import { useToast } from "~/hooks/use-toast";
import type {
  FolderWithBates,
  UpdateProjectBodyWithBates,
} from "~/types/bates";
import { trackEvent } from "~/utils/analytics";
import {
  PipelineMode,
  PipelineModeSelector,
} from "../../cases/components/PipelineModeSelector";
import { createCaseSchema } from "../../cases/validations/create-case";

interface EditCaseDialogProps {
  open: boolean;
  setOpen: (open: boolean) => void;
  projectId: string;
}

enum CASE_EDIT_STEP {
  FILL_FORM = 1,
  PIPELINE_MODE = 2,
}

type EditCaseFormData = z.infer<ReturnType<typeof createCaseSchema>>;

export const EditCaseDialog: FC<EditCaseDialogProps> = ({
  open,
  setOpen,
  projectId,
}) => {
  const intl = useIntl();
  const { organizationId } = useOrganizationParams();
  const { data: project } = useGetProject(organizationId, projectId);
  const { data: organizationSettings } =
    useGetOrganizationSettings(organizationId);
  const [dataLoaded, setDataLoaded] = useState(false);
  const [currentStep, setCurrentStep] = useState(CASE_EDIT_STEP.FILL_FORM);
  const [selectedPipelineMode, setSelectedPipelineMode] =
    useState<PipelineMode>("USE_SMART_AGENT");
  const [caseBatesEnabled, setCaseBatesEnabled] = useState<boolean | null>(
    null
  );
  const [batesToggleTouched, setBatesToggleTouched] = useState(false);
  const { toast } = useToast();

  const {
    register,
    handleSubmit,
    formState: { isValid, errors },
    control,
    reset,
    trigger,
  } = useForm<EditCaseFormData>({
    resolver: zodResolver(createCaseSchema(intl)),
    defaultValues: {
      name: "",
      claimNumber: "",
      dateOfBirth: undefined,
      dateOfLoss: undefined,
    },
    mode: "onChange",
  });

  useEffect(() => {
    if (project) {
      const parsedDOB = project.dateOfBirth ? dayjs(project.dateOfBirth) : null;
      const parsedDOL = project.dateOfLoss ? dayjs(project.dateOfLoss) : null;
      reset({
        name: project.name,
        claimNumber: project.claimNumber ?? "",
        dateOfBirth: parsedDOB?.isValid() ? parsedDOB.toDate() : undefined,
        dateOfLoss: parsedDOL?.isValid() ? parsedDOL.toDate() : undefined,
      });
      trigger();
      if (project.pipelineMode) {
        setSelectedPipelineMode(project.pipelineMode as PipelineMode);
      } else if (organizationSettings?.pipelineMode) {
        setSelectedPipelineMode(
          organizationSettings.pipelineMode as PipelineMode
        );
      }
      setDataLoaded(true);
    }
  }, [project, reset, organizationSettings?.pipelineMode]);

  useEffect(() => {
    if (!batesToggleTouched && project) {
      setCaseBatesEnabled(!(project as FolderWithBates).batesNumberingDisabled);
    }
  }, [project, batesToggleTouched]);

  const { mutate: updateProject, isPending } = useUpdateProject();
  const queryClient = useQueryClient();

  const updateCase = (data: EditCaseFormData) => {
    const body: UpdateProjectBodyWithBates = {
      name: data.name,
      claimNumber: data.claimNumber || "",
      dateOfBirth: data.dateOfBirth
        ? dayjs(data.dateOfBirth).format("YYYY-MM-DD")
        : "",
      dateOfLoss: data.dateOfLoss
        ? dayjs(data.dateOfLoss).format("YYYY-MM-DD")
        : null,
      pipelineMode: selectedPipelineMode,
      ...(batesToggleTouched && caseBatesEnabled !== null
        ? { batesNumberingDisabled: !caseBatesEnabled }
        : {}),
    };

    trackEvent({
      eventName: "Case_Updated",
      properties: {
        caseId: projectId,
        pipelineMode: selectedPipelineMode,
        source: "EditCaseDialog",
      },
    });

    updateProject(
      {
        organizationId,
        projectId,
        data: body,
      },
      {
        onSuccess: () => {
          queryClient.invalidateQueries({
            queryKey: getGetProjectQueryKey(organizationId, projectId),
          });
          queryClient.invalidateQueries({
            queryKey: getListProjectsQueryKey(organizationId),
          });
          setOpen(false);
          setCurrentStep(CASE_EDIT_STEP.FILL_FORM);
        },
        onError: () => {
          toast({
            variant: "destructive",
            title: intl.formatMessage({ id: "error" }),
            description: intl.formatMessage({
              id: "casesList.editCaseDialog.updateError",
              defaultMessage:
                "We couldn't save your changes. Please try again.",
            }),
          });
        },
      }
    );
  };

  const onSubmit = handleSubmit((data) => {
    if (currentStep === CASE_EDIT_STEP.FILL_FORM) {
      setCurrentStep(CASE_EDIT_STEP.PIPELINE_MODE);
    } else {
      updateCase(data);
    }
  });

  const handleBack = () => {
    setCurrentStep(CASE_EDIT_STEP.FILL_FORM);
  };

  const handleCancel = () => {
    trackEvent({
      eventName: "app/case_edit_modal/cancel.button/click",
      properties: {
        step: currentStep,
      },
    });
    setOpen(false);
    setCurrentStep(CASE_EDIT_STEP.FILL_FORM);
    setCaseBatesEnabled(null);
    setBatesToggleTouched(false);
  };

  const handleDialogOpenChange = (newOpen: boolean) => {
    setOpen(newOpen);
    if (!newOpen) {
      setCurrentStep(CASE_EDIT_STEP.FILL_FORM);
      setCaseBatesEnabled(null);
      setBatesToggleTouched(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleDialogOpenChange}>
      <DialogContent className="sm:max-w-[608px]">
        <DialogHeader>
          <DialogTitle>
            <FormattedMessage id="editCase" />
          </DialogTitle>
          <EditCaseFormSubtitle currentStep={currentStep} intl={intl} />
        </DialogHeader>
        <form onSubmit={onSubmit}>
          {currentStep === CASE_EDIT_STEP.FILL_FORM && (
            <>
              <div
                className={`flex flex-col gap-4 mb-4 ${!dataLoaded ? "opacity-50" : ""}`}
              >
                <div className="flex flex-col gap-2">
                  <Label htmlFor="caseName">
                    <FormattedMessage id="casesList.createCaseDialog.caseNameLabel" />
                  </Label>
                  <Input
                    id="caseName"
                    placeholder={intl.formatMessage({
                      id: "casesList.createCaseDialog.caseNamePlaceholder",
                    })}
                    className="shadow-none"
                    {...register("name")}
                    disabled={!dataLoaded}
                  />
                  {errors.name && dataLoaded && (
                    <span className="text-red-500 text-sm">
                      {errors.name.message}
                    </span>
                  )}
                </div>

                <div className="flex flex-col gap-2">
                  <Label htmlFor="dateOfBirth">
                    <FormattedMessage
                      id="dateOfBirthLabelOptional"
                      defaultMessage="Date of Birth (optional)"
                    />
                  </Label>
                  <Controller
                    name="dateOfBirth"
                    control={control}
                    render={({ field }) => (
                      <DayPicker
                        value={field.value || null}
                        onChange={field.onChange}
                        placeholder="MM/DD/YYYY"
                        disabled={!dataLoaded}
                        minDate={new Date("1900-01-01")}
                        maxDate={new Date()}
                      />
                    )}
                  />
                  {errors.dateOfBirth && dataLoaded && (
                    <span className="text-red-500 text-sm">
                      {errors.dateOfBirth.message}
                    </span>
                  )}
                </div>
                <div className="flex flex-col gap-2">
                  <Label htmlFor="dateOfLoss">
                    <FormattedMessage
                      id="dateOfLossLabelOptional"
                      defaultMessage="Date of Loss (optional)"
                    />
                  </Label>
                  <Controller
                    name="dateOfLoss"
                    control={control}
                    render={({ field }) => (
                      <DayPicker
                        value={field.value || null}
                        onChange={field.onChange}
                        placeholder="MM/DD/YYYY"
                        disabled={!dataLoaded}
                        minDate={new Date("1900-01-01")}
                        maxDate={new Date()}
                      />
                    )}
                  />
                  {errors.dateOfLoss && dataLoaded && (
                    <span className="text-red-500 text-sm">
                      {errors.dateOfLoss.message}
                    </span>
                  )}
                </div>
                <div className="flex flex-col gap-2">
                  <Label htmlFor="claimNumber">
                    <FormattedMessage
                      id="claimNumberLabelOptional"
                      defaultMessage="Claim/Case # (optional)"
                    />
                  </Label>
                  <Input
                    id="claimNumber"
                    placeholder="235848593"
                    className="shadow-none"
                    {...register("claimNumber")}
                    disabled={!dataLoaded}
                  />
                  {errors.claimNumber && dataLoaded && (
                    <span className="text-red-500 text-sm">
                      {errors.claimNumber.message}
                    </span>
                  )}
                </div>
                <div className="flex flex-col gap-3">
                  <div className="flex items-center justify-between">
                    <div>
                      <Label htmlFor="caseBatesEnabled">
                        <FormattedMessage
                          id="casesList.createCaseDialog.batesNumberingLabel"
                          defaultMessage="Bates Numbering"
                        />
                      </Label>
                      <p className="text-sm text-muted-foreground mt-1">
                        <FormattedMessage
                          id="casesList.createCaseDialog.batesNumberingHint"
                          defaultMessage="Enable to assign sequential page identifiers for this case."
                        />
                      </p>
                    </div>
                    <Switch
                      id="caseBatesEnabled"
                      checked={caseBatesEnabled === true}
                      disabled={!dataLoaded}
                      onCheckedChange={(checked) => {
                        setCaseBatesEnabled(checked);
                        setBatesToggleTouched(true);
                        trackEvent({
                          eventName:
                            "app/case_edit_modal/bates_numbering.toggle",
                          properties: { enabled: checked },
                        });
                      }}
                    />
                  </div>
                </div>
              </div>
              <div className="flex justify-end gap-2">
                <Button type="button" variant="outline" onClick={handleCancel}>
                  <FormattedMessage id="cancel" />
                </Button>
                <Button
                  disabled={!isValid || !dataLoaded}
                  type="submit"
                  onClick={() => {
                    trackEvent({
                      eventName:
                        "app/case_edit_modal/step_1/continue.button/click",
                    });
                  }}
                >
                  <FormattedMessage
                    id="casesList.createCaseDialog.continue"
                    defaultMessage="Continue"
                  />
                </Button>
              </div>
            </>
          )}
          {currentStep === CASE_EDIT_STEP.PIPELINE_MODE && (
            <>
              <div
                className={`flex flex-col gap-4 mb-4 ${!dataLoaded ? "opacity-50" : ""}`}
              >
                <PipelineModeSelector
                  selectedMode={selectedPipelineMode}
                  onModeChange={setSelectedPipelineMode}
                  disabled={!dataLoaded}
                />
              </div>
              <div className="flex justify-between">
                <Button type="button" variant="secondary" onClick={handleBack}>
                  <ChevronLeftIcon className="w-4 h-4 mr-2" />
                  <FormattedMessage
                    id="editInformation"
                    defaultMessage="Edit"
                  />
                </Button>
                <div className="flex gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={handleCancel}
                  >
                    <FormattedMessage id="cancel" />
                  </Button>
                  <Button
                    disabled={isPending || !isValid || !dataLoaded}
                    type="submit"
                    onClick={() => {
                      trackEvent({
                        eventName:
                          "app/case_edit_modal/step_2/save.button/click",
                        properties: {
                          pipelineMode: selectedPipelineMode,
                        },
                      });
                    }}
                  >
                    {isPending ? (
                      <FormattedMessage id="saving" />
                    ) : (
                      <FormattedMessage id="save" />
                    )}
                  </Button>
                </div>
              </div>
            </>
          )}
        </form>
      </DialogContent>
    </Dialog>
  );
};

const EditCaseFormSubtitle = ({
  currentStep,
  intl,
}: {
  currentStep: CASE_EDIT_STEP;
  intl: IntlShape;
}) => {
  return (
    <>
      {currentStep === CASE_EDIT_STEP.FILL_FORM && (
        <div>
          <span className="text-muted-foreground text-sm font-semibold leading-tight">
            {intl.formatMessage({
              id: "casesList.createCaseDialog.step1",
            })}
          </span>
          <span className="text-muted-foreground text-sm font-normal leading-tight">
            :{" "}
            {intl.formatMessage({
              id: "casesList.editCaseDialog.editDetails",
              defaultMessage: "Edit case details",
            })}
          </span>
        </div>
      )}
      {currentStep === CASE_EDIT_STEP.PIPELINE_MODE && (
        <div>
          <span className="text-muted-foreground text-sm font-semibold leading-tight">
            {intl.formatMessage({
              id: "casesList.createCaseDialog.step2",
            })}
          </span>
          <span className="text-muted-foreground text-sm font-normal leading-tight">
            :{" "}
            {intl.formatMessage({
              id: "casesList.createCaseDialog.selectPipelineMode",
            })}
          </span>
        </div>
      )}
    </>
  );
};
